import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";

import ControlledRadioGroupSeparatedLabel from "../../../../../components/controlled/ControlledRadioGroupSeparatedLabel";
import ControlledTextFieldSeparatedLabel from "../../../../../components/controlled/ControlledTextFieldSeparatedLabel";

import { useAddUserToCompanyMutation } from "../../../../user/userApiSlice";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GET_USER_ROLES_OPTIONS } from "../../../../../constants";
import { getFormSchema } from "./formSchema";

const AddDialog = ({ onClose, open }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { companyId } = useParams();

	const [addUser, { isLoading: isAdding }] = useAddUserToCompanyMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const submitNewUser = async ({
		firstName,
		lastName,
		email,
		// phone,
		// branch,
		role,
	}) => {
		if (!companyId) {
			enqueueSnackbar(t("company_not_found"), {
				variant: "error",
				preventDuplicate: true,
			});

			return;
		}

		await addUser({
			companyId: companyId,
			data: {
				firstName,
				lastName,
				email,
				// phone: phone.replaceAll(" ", ""),
				// branch,
				role,
			},
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("added_user"), {
					variant: "success",
				});
				onClose();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				enqueueSnackbar(err.message, {
					variant: "error",
					preventDuplicate: true,
				});
			});
		}
	}, [errors, enqueueSnackbar]);

	// if (isLoading) {
	// 	return <h1>Loading...</h1>;
	// }

	return (
		<Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(submitNewUser)}>
					<Grid container spacing={1.5}>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="firstName"
								label={t("first_name_label")}
								error={!!errors?.firstName}
								required
							/>
						</Grid>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="lastName"
								label={t("surname_label")}
								error={!!errors?.lastName}
								required
							/>
						</Grid>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="email"
								type="email"
								label={t("email_and_login_label")}
								error={!!errors?.email}
								required
							/>
						</Grid>
						{/* <Grid item xs={12}>
							<ControlledPhoneInputSeparatedLabel
								control={control}
								name="phone"
								type="tel"
								label={t("phone_label")}
								error={!!errors?.phone}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<ControlledSelectSeparatedLabel
								control={control}
								name="branch"
								label={t("branch")}
								options={branches.map((branch) => {
									return { value: branch.id, label: branch.name };
								})}
								error={!!errors?.branch}
							/>
						</Grid> */}
						<Grid size={12}>
							<ControlledRadioGroupSeparatedLabel
								className="user-role-radio-group"
								control={control}
								name="role"
								label={t("permission_type")}
								options={GET_USER_ROLES_OPTIONS(t)}
								error={!!errors?.role}
							/>
						</Grid>
						<Grid size={12}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Button loading={isAdding} type="submit" variant="contained">
									{t("add")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Dialog>
	);
};

export default AddDialog;
